import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import MainLayout from "../layout/MainLayout";
import { useAlertContext } from "../context/AlertContext";
import { DEPARTMENTS } from "../config/api";
import AdaptationQuestionFields from "../adaptation/AdaptationQuestionFields";
import AdaptationHelp from "../adaptation/AdaptationHelp";
import {
    copyForm,
    createForm,
    createQuestion,
    createRole,
    createRule,
    getBuilderState,
    getDashboard,
    previewRule,
    updateForm,
    updateQuestion,
    updateRole,
    updateRule,
} from "../services/adaptationBuilderApi";

/**
 * Конструктор адаптации — [Claude] 2026-11-02, Чат 3, правка №7.
 * Этапы (кому / когда / участники), анкеты (вопросы, обязательность, условия показа),
 * библиотека вопросов (гибкие шкалы, сквозные вопросы, риск) и участники (свои роли).
 */

const TABS = [
    ["stages", "Этапы"],
    ["forms", "Анкеты"],
    ["questions", "Вопросы"],
    ["roles", "Участники"],
];

const TYPE_LABELS = {
    scale: "Шкала",
    choice: "Один вариант",
    multi: "Несколько вариантов",
    text: "Текст",
    yes_no: "Да / нет",
    number: "Число",
};

const NUMERIC_OPS = [
    ["lte", "≤"],
    ["lt", "<"],
    ["gte", "≥"],
    ["gt", ">"],
    ["eq", "="],
    ["delta_down_gte", "снизилась к прошлому этапу на ≥"],
    ["delta_up_gte", "выросла к прошлому этапу на ≥"],
    ["delta_gte", "изменилась к прошлому этапу на ≥"],
    ["answered", "заполнен"],
];
const CHOICE_OPS = [
    ["in", "один из"],
    ["not_in", "не из"],
    ["answered", "заполнен"],
];
const TEXT_OPS = [["answered", "заполнен"]];

const ACTION_LABELS = {
    create: "будет создан",
    update: "анкета обновится",
    remove: "будет снят",
    keep: "без изменений",
    restore: "вернётся стандартная анкета",
    conflict: "занят другим правилом",
};

// Компактная сетка: одна высота контролов (h-8), минимум отступов. [Claude] 2026-11-02, правка дизайна №7
const field = "h-8 rounded-md border border-slate-200 bg-white px-2 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100";
const input = `${field} w-full`;
const area = "w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100";
const btn = "inline-flex h-8 items-center justify-center rounded-md px-3 text-sm font-semibold transition disabled:opacity-50";
const btnPrimary = `${btn} bg-slate-900 text-white hover:bg-slate-700`;
const btnGhost = `${btn} border border-slate-200 bg-white text-slate-700 hover:border-slate-400`;
const chip = (active) =>
    `rounded-full border px-2 py-0.5 text-xs font-medium transition ${active ? "border-amber-400 bg-amber-100 text-slate-900" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`;

function Field({ label, hint, children, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="mb-0.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
            {children}
            {hint ? <span className="mt-0.5 block text-[11px] leading-4 text-slate-400">{hint}</span> : null}
        </label>
    );
}

function Toggle({ checked, onChange, label }) {
    return (
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" className="h-4 w-4 accent-amber-500" checked={Boolean(checked)} onChange={(e) => onChange(e.target.checked)} />
            {label}
        </label>
    );
}

function Pane({ list, editor }) {
    return (
        <div className="grid min-h-0 flex-1 gap-2 lg:grid-cols-[260px_minmax(0,1fr)]">
            <aside className="min-h-0 overflow-y-auto rounded-lg border border-slate-200 bg-white">{list}</aside>
            <section className="min-h-0 overflow-y-auto rounded-lg border border-slate-200 bg-white p-3">{editor}</section>
        </div>
    );
}

function ListItem({ active, onClick, title, meta, badge }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`block w-full border-b border-slate-100 px-2.5 py-1.5 text-left transition ${active ? "bg-amber-50" : "hover:bg-slate-50"}`}
        >
            <span className="flex items-start justify-between gap-2">
                <span className="line-clamp-2 text-sm font-semibold text-slate-900">{title}</span>
                {badge ? <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-500">{badge}</span> : null}
            </span>
            {meta ? <span className="mt-0.5 block text-xs text-slate-500">{meta}</span> : null}
        </button>
    );
}

function errorText(e, fallback) {
    return e?.message || fallback;
}

// ------------------------------------------------------------------ questions

const EMPTY_QUESTION = {
    text: "",
    hint: "",
    type: "scale",
    cross: false,
    category: "",
    scale: { min: 1, max: 5, step: 1, min_label: "", max_label: "", labels: {}, special: [], higher_is_better: true },
    options: [],
    exclusive_options: [],
    number: { min: null, max: null },
    risk: { enabled: false, medium_below: 60, high_below: 40, drop: 25 },
};

function QuestionEditor({ question, onSaved }) {
    const { showAlert } = useAlertContext();
    const [draft, setDraft] = useState(EMPTY_QUESTION);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        setDraft(question ? { ...EMPTY_QUESTION, ...question, scale: { ...EMPTY_QUESTION.scale, ...(question.scale || {}) }, risk: { ...EMPTY_QUESTION.risk, ...(question.risk || {}) } } : EMPTY_QUESTION);
    }, [question]);
    const set = (patch) => setDraft((prev) => ({ ...prev, ...patch }));
    const setScale = (patch) => setDraft((prev) => ({ ...prev, scale: { ...prev.scale, ...patch } }));
    const setRisk = (patch) => setDraft((prev) => ({ ...prev, risk: { ...prev.risk, ...patch } }));
    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...draft };
            const saved = question?.id ? await updateQuestion(question.id, payload) : await createQuestion(payload);
            showAlert(question?.id ? "Вопрос обновлён. Уже отправленные анкеты не изменились." : "Вопрос добавлен", "success");
            onSaved(saved);
        } catch (e) {
            showAlert(errorText(e, "Не удалось сохранить вопрос"), "error");
        } finally {
            setSaving(false);
        }
    };
    const preview = { ...draft, id: "preview", special_options: draft.scale?.special || [] };
    const [previewValue, setPreviewValue] = useState({});
    const specialsText = (draft.scale?.special || []).map((s) => s.label).join("\n");
    return (
        <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_340px]">
            <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <h2 className="text-base font-bold text-slate-900">{question?.id ? `Вопрос ${question.code}` : "Новый вопрос"}</h2>
                    {question?.version ? <span className="text-xs text-slate-400">версия {question.version}{question.source === "catalog" ? " · из утверждённых анкет" : ""}</span> : null}
                </div>
                <Field label="Текст вопроса">
                    <textarea className={`${area} min-h-14`} value={draft.text} onChange={(e) => set({ text: e.target.value })} />
                </Field>
                <Field label="Подсказка (необязательно)">
                    <input className={input} value={draft.hint || ""} onChange={(e) => set({ hint: e.target.value })} />
                </Field>
                <div className="grid gap-2 sm:grid-cols-2">
                    <Field label="Тип ответа">
                        <select className={input} value={draft.type} onChange={(e) => set({ type: e.target.value })} disabled={Boolean(question?.cross)}>
                            {Object.entries(TYPE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                    </Field>
                    <Field label="Раздел (для группировки)">
                        <input className={input} value={draft.category || ""} onChange={(e) => set({ category: e.target.value })} placeholder="например, «Нагрузка»" />
                    </Field>
                </div>
                {draft.type === "scale" ? (
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
                        <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">Шкала</p>
                        <div className="grid gap-2 sm:grid-cols-3 2xl:grid-cols-6">
                            <Field label="От"><input type="number" className={input} value={draft.scale.min} onChange={(e) => setScale({ min: e.target.value })} /></Field>
                            <Field label="До"><input type="number" className={input} value={draft.scale.max} onChange={(e) => setScale({ max: e.target.value })} /></Field>
                            <Field label="Шаг"><input type="number" step="0.5" className={input} value={draft.scale.step} onChange={(e) => setScale({ step: e.target.value })} /></Field>
                            <Field label="Подпись минимума"><input className={input} value={draft.scale.min_label} onChange={(e) => setScale({ min_label: e.target.value })} placeholder="совсем нет" /></Field>
                            <Field label="Подпись максимума"><input className={input} value={draft.scale.max_label} onChange={(e) => setScale({ max_label: e.target.value })} placeholder="полностью" /></Field>
                            <Field label="Что лучше">
                                <select className={input} value={draft.scale.higher_is_better ? "1" : "0"} onChange={(e) => setScale({ higher_is_better: e.target.value === "1" })}>
                                    <option value="1">больше — лучше</option>
                                    <option value="0">меньше — лучше</option>
                                </select>
                            </Field>
                        </div>
                        <Field label="Особые ответы (по одному в строке)" hint="Например: «не применимо», «недостаточно данных». В динамику и риск не идут." className="mt-2">
                            <textarea
                                className={`${area} min-h-12`}
                                value={specialsText}
                                onChange={(e) => setScale({
                                    special: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean).map((label, i) => ({ value: (draft.scale.special || [])[i]?.label === label ? draft.scale.special[i].value : label, label })),
                                })}
                            />
                        </Field>
                    </div>
                ) : null}
                {draft.type === "choice" || draft.type === "multi" ? (
                    <Field label="Варианты (по одному в строке)">
                        <textarea
                            className={`${area} min-h-24`}
                            value={(draft.options || []).join("\n")}
                            onChange={(e) => set({ options: e.target.value.split("\n") })}
                        />
                    </Field>
                ) : null}
                {draft.type === "multi" && (draft.options || []).filter(Boolean).length ? (
                    <Field label="Варианты, которые нельзя выбрать вместе с другими">
                        <div className="flex flex-wrap gap-1.5">
                            {(draft.options || []).filter((o) => o.trim()).map((o) => {
                                const active = (draft.exclusive_options || []).includes(o.trim());
                                return (
                                    <button key={o} type="button" className={chip(active)} onClick={() => set({
                                        exclusive_options: active ? draft.exclusive_options.filter((x) => x !== o.trim()) : [...(draft.exclusive_options || []), o.trim()],
                                    })}>{o}</button>
                                );
                            })}
                        </div>
                    </Field>
                ) : null}
                {draft.type === "number" ? (
                    <div className="grid gap-2 sm:grid-cols-2">
                        <Field label="Минимум"><input type="number" className={input} value={draft.number?.min ?? ""} onChange={(e) => set({ number: { ...draft.number, min: e.target.value } })} /></Field>
                        <Field label="Максимум"><input type="number" className={input} value={draft.number?.max ?? ""} onChange={(e) => set({ number: { ...draft.number, max: e.target.value } })} /></Field>
                    </div>
                ) : null}
                <div className="rounded-md border border-slate-200 p-2">
                    <Toggle
                        checked={draft.cross}
                        onChange={(v) => set({ cross: v })}
                        label="Сквозной вопрос — повторяется на этапах, по нему строится динамика"
                    />
                    {draft.cross && !["scale", "yes_no", "number"].includes(draft.type) ? (
                        <p className="mt-1 text-xs text-red-600">Сквозным может быть только вопрос со шкалой, «да/нет» или числом.</p>
                    ) : null}
                    {question?.cross ? <p className="mt-1 text-xs text-slate-500">Тип сквозного вопроса менять нельзя — иначе динамика станет несравнимой.</p> : null}
                </div>
                {draft.type === "scale" || draft.type === "yes_no" ? (
                    <div className="rounded-md border border-slate-200 p-2">
                        <Toggle checked={draft.risk.enabled} onChange={(v) => setRisk({ enabled: v })} label="Влияет на риск" />
                        {draft.risk.enabled ? (
                            <div className="mt-2 grid gap-2 sm:grid-cols-3">
                                <Field label="Средний риск, если ниже, %"><input type="number" className={input} value={draft.risk.medium_below} onChange={(e) => setRisk({ medium_below: e.target.value })} /></Field>
                                <Field label="Высокий риск, если ниже, %"><input type="number" className={input} value={draft.risk.high_below} onChange={(e) => setRisk({ high_below: e.target.value })} /></Field>
                                <Field label="Падение к прошлому этапу, %" hint="≥ порога — средний, ≥ 2× — высокий"><input type="number" className={input} value={draft.risk.drop} onChange={(e) => setRisk({ drop: e.target.value })} /></Field>
                            </div>
                        ) : null}
                    </div>
                ) : null}
                <div className="flex gap-2">
                    <button type="button" className={btnPrimary} disabled={saving} onClick={save}>{saving ? "Сохраняю…" : "Сохранить"}</button>
                </div>
            </div>
            <div className="rounded-lg bg-slate-50 p-2 xl:sticky xl:top-0 xl:self-start xl:max-h-[calc(100vh-8rem)] xl:overflow-y-auto">
                <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">Как увидит участник</p>
                <AdaptationQuestionFields
                    questions={[{ ...preview, options: (preview.options || []).map((o) => o.trim()).filter(Boolean), show_if: undefined }]}
                    values={previewValue}
                    onChange={(id, v) => setPreviewValue({ ...previewValue, [id]: v })}
                    coreHistory={{}}
                    disabled={false}
                    compact
                />
            </div>
        </div>
    );
}

function QuestionsTab({ state, reload }) {
    const [selected, setSelected] = useState(null);
    const [query, setQuery] = useState("");
    const [onlyCross, setOnlyCross] = useState(false);
    const [showArchived, setShowArchived] = useState(false);
    const list = useMemo(() => (state.questions || []).filter((q) =>
        (showArchived || !q.archived)
        && (!onlyCross || q.cross)
        && (!query || `${q.text} ${q.code} ${q.category || ""}`.toLowerCase().includes(query.toLowerCase()))
    ), [state.questions, query, onlyCross, showArchived]);
    const current = selected === "new" ? null : (state.questions || []).find((q) => q.id === selected) || null;
    return (
        <Pane
            list={
                <div>
                    <div className="sticky top-0 z-10 space-y-2 border-b border-slate-200 bg-white p-2">
                        <div className="flex gap-2">
                            <input className={input} placeholder="Поиск вопроса" value={query} onChange={(e) => setQuery(e.target.value)} />
                            <button type="button" className={btnPrimary} onClick={() => setSelected("new")}>+</button>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <Toggle checked={onlyCross} onChange={setOnlyCross} label="только сквозные" />
                            <Toggle checked={showArchived} onChange={setShowArchived} label="архив" />
                        </div>
                    </div>
                    {list.map((q) => (
                        <ListItem
                            key={q.id}
                            active={selected === q.id}
                            onClick={() => setSelected(q.id)}
                            title={q.text}
                            meta={`${TYPE_LABELS[q.type] || q.type}${q.type === "scale" && q.scale ? ` ${q.scale.min}–${q.scale.max}` : ""}${q.category ? ` · ${q.category}` : ""}`}
                            badge={q.cross ? "сквозной" : q.archived ? "архив" : null}
                        />
                    ))}
                    {!list.length ? <p className="p-4 text-sm text-slate-500">Ничего не найдено</p> : null}
                </div>
            }
            editor={
                selected == null ? (
                    <p className="text-sm text-slate-500">Выберите вопрос слева или создайте новый (+). Утверждённые анкеты уже перенесены в библиотеку — их можно править и переиспользовать.</p>
                ) : (
                    <QuestionEditor question={current} onSaved={async (saved) => { await reload(); setSelected(saved.id); }} />
                )
            }
        />
    );
}

// ------------------------------------------------------------------- conditions

function opsFor(question) {
    if (!question) return TEXT_OPS;
    if (["scale", "number", "yes_no", "scale_1_5", "scale_na", "scale_obs"].includes(question.type)) return NUMERIC_OPS;
    if (["choice", "multi"].includes(question.type)) return CHOICE_OPS;
    return TEXT_OPS;
}

function parseCondition(showIf) {
    if (!showIf) return { mode: "any", rules: [] };
    const flat = (list) => list.every((r) => r && r.field);
    if (Array.isArray(showIf.any) && flat(showIf.any)) return { mode: "any", rules: showIf.any.map(ruleToRow) };
    if (Array.isArray(showIf.all) && flat(showIf.all)) return { mode: "all", rules: showIf.all.map(ruleToRow) };
    if (showIf.field) return { mode: "any", rules: [ruleToRow(showIf)] };
    return { mode: "complex", rules: [], raw: showIf };
}

function ruleToRow(rule) {
    const op = Object.keys(rule).find((k) => k !== "field") || "answered";
    return { field: rule.field, op, value: rule[op] };
}

function rowsToCondition(mode, rows) {
    const rules = rows.filter((r) => r.field && r.op).map((r) => {
        let value = r.value;
        if (r.op === "answered") value = r.value !== false;
        else if (["in", "not_in"].includes(r.op)) value = Array.isArray(r.value) ? r.value : [];
        else if (r.op === "eq") value = r.value !== "" && r.value != null && !Number.isNaN(Number(r.value)) ? Number(r.value) : r.value;
        else value = r.value === "" || r.value == null ? r.value : Number(r.value);
        return { field: r.field, [r.op]: value };
    });
    if (!rules.length) return null;
    return { [mode === "all" ? "all" : "any"]: rules };
}

function ConditionEditor({ value, onChange, earlier }) {
    // Local rows keep half-filled conditions; the parent only receives complete rules.
    const [parsed] = useState(() => parseCondition(value));
    const [mode, setMode] = useState(parsed.mode === "complex" ? "any" : parsed.mode);
    const [rows, setRows] = useState(parsed.rules);
    const push = (nextMode, nextRows) => {
        setMode(nextMode);
        setRows(nextRows);
        onChange(rowsToCondition(nextMode, nextRows));
    };
    if (parsed.mode === "complex" && value) {
        return (
            <div className="rounded-lg bg-slate-50 p-2 text-xs text-slate-600">
                Сложное условие из утверждённой анкеты сохранено как есть.
                <button type="button" className="ml-2 font-semibold text-amber-700 underline" onClick={() => onChange(null)}>Сбросить и настроить заново</button>
            </div>
        );
    }
    return (
        <div className="space-y-1">
            {rows.map((row, index) => {
                const target = earlier.find((q) => q.code === row.field);
                const ops = opsFor(target);
                const options = target?.type === "yes_no" ? ["да", "нет"] : (target?.options || []);
                return (
                    <div key={index} className="flex flex-wrap items-center gap-1">
                        {index > 0 ? <span className="w-8 text-xs font-bold text-slate-500">{mode === "all" ? "И" : "ИЛИ"}</span> : <span className="w-8 text-xs font-bold text-slate-500">если</span>}
                        <select className={`${field} min-w-[200px] flex-1`} value={row.field || ""} onChange={(e) => {
                            const next = [...rows];
                            const q = earlier.find((x) => x.code === e.target.value);
                            next[index] = { field: e.target.value, op: opsFor(q)[0][0], value: "" };
                            push(mode, next);
                        }}>
                            <option value="">— вопрос выше —</option>
                            {earlier.map((q) => <option key={q.code} value={q.code}>{q.text.slice(0, 70)}</option>)}
                        </select>
                        <select className={`${field} w-auto`} value={row.op} onChange={(e) => {
                            const next = [...rows];
                            next[index] = { ...row, op: e.target.value, value: ["in", "not_in"].includes(e.target.value) ? [] : "" };
                            push(mode, next);
                        }}>
                            {ops.map(([op, label]) => <option key={op} value={op}>{label}</option>)}
                        </select>
                        {row.op === "answered" ? null : ["in", "not_in"].includes(row.op) ? (
                            <span className="flex flex-wrap gap-1">
                                {options.map((o) => {
                                    const list = Array.isArray(row.value) ? row.value : [];
                                    const active = list.includes(o);
                                    return <button key={o} type="button" className={chip(active)} onClick={() => {
                                        const next = [...rows];
                                        next[index] = { ...row, value: active ? list.filter((x) => x !== o) : [...list, o] };
                                        push(mode, next);
                                    }}>{o}</button>;
                                })}
                            </span>
                        ) : row.op === "eq" && options.length ? (
                            <select className={`${field} w-auto`} value={row.value ?? ""} onChange={(e) => {
                                const next = [...rows];
                                next[index] = { ...row, value: e.target.value };
                                push(mode, next);
                            }}>
                                <option value="">—</option>
                                {options.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                        ) : (
                            <input type="number" className={`${field} w-24`} value={row.value ?? ""} onChange={(e) => {
                                const next = [...rows];
                                next[index] = { ...row, value: e.target.value };
                                push(mode, next);
                            }} />
                        )}
                        <button type="button" className="px-2 text-slate-400 hover:text-red-600" title="Убрать" onClick={() => push(mode, rows.filter((_, i) => i !== index))}>×</button>
                    </div>
                );
            })}
            <div className="flex flex-wrap items-center gap-2">
                <button type="button" className="text-xs font-semibold text-amber-700 hover:underline" disabled={!earlier.length} onClick={() => push(mode, [...rows, { field: "", op: "lte", value: "" }])}>
                    + условие показа
                </button>
                {rows.length > 1 ? (
                    <select className="rounded border border-slate-200 px-1 py-0.5 text-xs" value={mode} onChange={(e) => push(e.target.value, rows)}>
                        <option value="any">достаточно любого (ИЛИ)</option>
                        <option value="all">нужны все (И)</option>
                    </select>
                ) : null}
                {!earlier.length ? <span className="text-xs text-slate-400">условия ссылаются на вопросы выше</span> : null}
            </div>
        </div>
    );
}

// ------------------------------------------------------------------------ forms

function snapshotQuestion(q, item) {
    return {
        id: q.code,
        text: q.text,
        hint: q.hint,
        type: q.type,
        required: item.required !== false,
        options: q.options,
        exclusive_options: q.exclusive_options,
        scale: q.scale,
        special_options: q.scale?.special || [],
        number: q.number,
        show_if: item.show_if || undefined,
    };
}

function FormEditor({ form, questions, onSaved }) {
    const { showAlert } = useAlertContext();
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [items, setItems] = useState([]);
    const [adding, setAdding] = useState("");
    const [saving, setSaving] = useState(false);
    const [values, setValues] = useState({});
    const [history, setHistory] = useState({});
    useEffect(() => {
        setName(form?.name || "");
        setDescription(form?.description || "");
        setItems((form?.items || []).map((i) => ({ ...i })));
        setValues({});
        setHistory({});
    }, [form]);
    const byId = useMemo(() => new Map((questions || []).map((q) => [q.id, q])), [questions]);
    const used = new Set(items.map((i) => i.question_id));
    const move = (index, delta) => {
        const next = [...items];
        const target = index + delta;
        if (target < 0 || target >= next.length) return;
        [next[index], next[target]] = [next[target], next[index]];
        setItems(next);
    };
    const save = async () => {
        setSaving(true);
        try {
            const payload = { name, description, items };
            const saved = form?.id ? await updateForm(form.id, payload) : await createForm(payload);
            showAlert(form?.id ? "Анкета сохранена. Новая версия попадёт только в будущие незаполненные этапы." : "Анкета создана", "success");
            onSaved(saved);
        } catch (e) {
            showAlert(errorText(e, "Не удалось сохранить анкету"), "error");
        } finally {
            setSaving(false);
        }
    };
    const previewQuestions = items.map((item) => byId.get(item.question_id)).filter(Boolean).map((q, i) => snapshotQuestion(q, items[i]));
    const crossInForm = items.map((i) => byId.get(i.question_id)).filter((q) => q && q.cross);
    return (
        <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_360px]">
            <div className="space-y-2">
                <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                    <Field label="Название анкеты"><input className={input} value={name} onChange={(e) => setName(e.target.value)} /></Field>
                    <Field label="Описание"><input className={input} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
                </div>
                <div className="space-y-1">
                    {items.map((item, index) => {
                        const q = byId.get(item.question_id);
                        const earlier = items.slice(0, index).map((i) => byId.get(i.question_id)).filter(Boolean);
                        return (
                            <div key={`${form?.id || "new"}-${item.question_id}`} className="rounded-md border border-slate-200 px-2 py-1.5 hover:border-slate-300">
                                <div className="flex items-start gap-2">
                                    <span className="mt-0.5 w-6 shrink-0 text-right text-xs font-bold text-slate-400">{index + 1}</span>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold text-slate-900">
                                            {q ? q.text : `Вопрос #${item.question_id} не найден`}
                                            {q?.cross ? <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-800">сквозной</span> : null}
                                        </p>
                                        <p className="text-[11px] text-slate-400">{q ? `${TYPE_LABELS[q.type] || q.type}${q.type === "scale" && q.scale ? ` ${q.scale.min}–${q.scale.max}` : ""}` : ""}</p>
                                        <div className="mt-1">
                                            <ConditionEditor
                                                value={item.show_if}
                                                earlier={earlier}
                                                onChange={(showIf) => setItems(items.map((it, i) => (i === index ? { ...it, show_if: showIf } : it)))}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex shrink-0 items-center gap-1">
                                        <Toggle checked={item.required !== false} onChange={(v) => setItems(items.map((it, i) => (i === index ? { ...it, required: v } : it)))} label="обяз." />
                                        <button type="button" className="px-1.5 text-slate-400 hover:text-slate-900" title="Выше" onClick={() => move(index, -1)}>↑</button>
                                        <button type="button" className="px-1.5 text-slate-400 hover:text-slate-900" title="Ниже" onClick={() => move(index, 1)}>↓</button>
                                        <button type="button" className="px-1.5 text-slate-400 hover:text-red-600" title="Убрать из анкеты" onClick={() => setItems(items.filter((_, i) => i !== index))}>×</button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="flex flex-wrap gap-2">
                    <select className={`${field} flex-1`} value={adding} onChange={(e) => setAdding(e.target.value)}>
                        <option value="">— добавить вопрос из библиотеки —</option>
                        {(questions || []).filter((q) => !q.archived && !used.has(q.id)).map((q) => (
                            <option key={q.id} value={q.id}>{q.cross ? "◆ " : ""}{q.text.slice(0, 110)}</option>
                        ))}
                    </select>
                    <button type="button" className={btnGhost} disabled={!adding} onClick={() => { setItems([...items, { question_id: Number(adding), required: true, show_if: null }]); setAdding(""); }}>Добавить</button>
                </div>
                <button type="button" className={btnPrimary} disabled={saving} onClick={save}>{saving ? "Сохраняю…" : "Сохранить анкету"}</button>
            </div>
            <div className="rounded-lg bg-slate-50 p-2 xl:sticky xl:top-0 xl:self-start xl:max-h-[calc(100vh-8rem)] xl:overflow-y-auto">
                <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">Предпросмотр — отвечайте, чтобы проверить условия</p>
                {crossInForm.length ? (
                    <div className="mb-2 rounded-md border border-dashed border-slate-300 bg-white p-1.5">
                        <p className="mb-1 text-xs text-slate-500">Ответы на прошлом этапе (для условий «снизилась / выросла»):</p>
                        <div className="grid grid-cols-1 gap-1">
                            {crossInForm.map((q) => (
                                <label key={q.code} className="flex items-center gap-1 text-xs text-slate-600">
                                    <span className="flex-1 truncate" title={q.text}>{q.text}</span>
                                    <input type="number" className="w-16 rounded border border-slate-200 px-1 py-0.5" value={history[q.code] ?? ""} onChange={(e) => setHistory({ ...history, [q.code]: e.target.value === "" ? undefined : Number(e.target.value) })} />
                                </label>
                            ))}
                        </div>
                    </div>
                ) : null}
                <AdaptationQuestionFields
                    questions={previewQuestions}
                    values={values}
                    onChange={(id, v) => setValues((prev) => ({ ...prev, [id]: v }))}
                    coreHistory={history}
                    disabled={false}
                    compact
                />
            </div>
        </div>
    );
}

function FormsTab({ state, reload }) {
    const { showAlert } = useAlertContext();
    const [selected, setSelected] = useState(null);
    const forms = (state.forms || []).filter((f) => !f.archived);
    const current = selected === "new" ? null : forms.find((f) => f.id === selected) || null;
    const copy = async () => {
        try {
            const saved = await copyForm(current.id);
            await reload();
            setSelected(saved.id);
            showAlert("Копия создана", "success");
        } catch (e) {
            showAlert(errorText(e, "Не удалось скопировать"), "error");
        }
    };
    return (
        <Pane
            list={
                <div>
                    <div className="sticky top-0 z-10 border-b border-slate-200 bg-white p-2">
                        <button type="button" className={`${btnPrimary} w-full`} onClick={() => setSelected("new")}>+ Новая анкета</button>
                    </div>
                    {forms.map((f) => (
                        <ListItem key={f.id} active={selected === f.id} onClick={() => setSelected(f.id)} title={f.name}
                            meta={`${(f.items || []).length} вопр. · версия ${f.version}`} badge={f.source === "catalog" ? "стандарт" : null} />
                    ))}
                </div>
            }
            editor={
                selected == null ? (
                    <p className="text-sm text-slate-500">Анкета — набор вопросов для одного участника этапа. Выберите слева или создайте новую. Стандартные анкеты можно скопировать и изменить.</p>
                ) : (
                    <div className="space-y-2">
                        {current ? (
                            <div className="flex justify-end">
                                <button type="button" className={btnGhost} onClick={copy}>Сделать копию</button>
                            </div>
                        ) : null}
                        <FormEditor form={current} questions={state.questions} onSaved={async (saved) => { await reload(); setSelected(saved.id); }} />
                    </div>
                )
            }
        />
    );
}

// ------------------------------------------------------------------------ roles

function RolesTab({ state, reload }) {
    const { showAlert } = useAlertContext();
    const [draft, setDraft] = useState({ name: "", short: "", description: "", default_person: "" });
    const [editing, setEditing] = useState(null);
    const save = async () => {
        try {
            if (editing) await updateRole(editing, draft);
            else await createRole(draft);
            setDraft({ name: "", short: "", description: "", default_person: "" });
            setEditing(null);
            await reload();
            showAlert("Участник сохранён", "success");
        } catch (e) {
            showAlert(errorText(e, "Не удалось сохранить"), "error");
        }
    };
    return (
        <div className="grid min-h-0 flex-1 gap-2 lg:grid-cols-[minmax(0,1fr)_340px]">
            <div className="overflow-y-auto rounded-lg border border-slate-200 bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                        <tr><th className="px-2 py-1.5">Участник</th><th className="px-2 py-1.5">Метка</th><th className="px-2 py-1.5">Кто заполняет по умолчанию</th><th className="px-2 py-1.5" /></tr>
                    </thead>
                    <tbody>
                        {(state.roles || []).map((r) => (
                            <tr key={r.id} className="border-t border-slate-100">
                                <td className="px-2 py-1.5"><b>{r.name}</b>{r.is_system ? <span className="ml-2 text-xs text-slate-400">системный</span> : null}{r.archived ? <span className="ml-2 text-xs text-red-500">в архиве</span> : null}<p className="text-xs text-slate-500">{r.description}</p></td>
                                <td className="px-2 py-1.5">{r.short}</td>
                                <td className="px-2 py-1.5 text-slate-600">{r.default_person || (r.is_system ? "определяется кейсом" : "—")}</td>
                                <td className="px-2 py-1.5 text-right">
                                    <button type="button" className="text-xs font-semibold text-amber-700 hover:underline" onClick={() => { setEditing(r.id); setDraft({ name: r.name, short: r.short, description: r.description || "", default_person: r.default_person || "" }); }}>изменить</button>
                                    {!r.is_system ? (
                                        <button type="button" className="ml-3 text-xs text-slate-500 hover:underline" onClick={async () => { await updateRole(r.id, { archived: !r.archived }); await reload(); }}>{r.archived ? "вернуть" : "в архив"}</button>
                                    ) : null}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-3 lg:self-start">
                <h2 className="text-base font-bold text-slate-900">{editing ? "Изменить участника" : "Новый участник"}</h2>
                <p className="text-xs text-slate-500">Например, наставник, руководитель руководителя, коллега. Заполняет анкету по ссылке без входа в платформу — ссылку отправляет HR.</p>
                <Field label="Название"><input className={input} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></Field>
                <Field label="Короткая метка (1–2 буквы)"><input className={input} maxLength={4} value={draft.short} onChange={(e) => setDraft({ ...draft, short: e.target.value })} /></Field>
                <Field label="Описание"><input className={input} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></Field>
                <Field label="Кто заполняет по умолчанию" hint="Можно изменить в конкретном этапе сотрудника"><input className={input} value={draft.default_person} onChange={(e) => setDraft({ ...draft, default_person: e.target.value })} /></Field>
                <div className="flex gap-2">
                    <button type="button" className={btnPrimary} disabled={!draft.name.trim()} onClick={save}>Сохранить</button>
                    {editing ? <button type="button" className={btnGhost} onClick={() => { setEditing(null); setDraft({ name: "", short: "", description: "", default_person: "" }); }}>Отмена</button> : null}
                </div>
            </div>
        </div>
    );
}

// ------------------------------------------------------------------------ stages

const EMPTY_RULE = {
    title: "",
    public_title: "",
    active: true,
    timing: { mode: "offset", unit: "months", value: 3, business_days: true, repeat: { frequency: "" } },
    scope: { mode: "all", departments: [], positions: [], enrollment_ids: [], routes: [], include_future: true, include_past_dates: false },
    participants: [{ role: "employee", form_id: "", required: true }],
};

function describeTiming(t, kinds) {
    if (!t) return "";
    const units = { days: "дн.", weeks: "нед.", months: "мес." };
    let text = "";
    if (t.mode === "offset") text = `через ${t.value} ${units[t.unit]} от выхода`;
    else if (t.mode === "date") text = `с ${new Date(t.date).toLocaleDateString("ru-RU")}`;
    else if (t.mode === "standard") text = `анкеты этапа «${(kinds || []).find((k) => k.kind === t.kind)?.label || t.kind}»`;
    const r = t.repeat;
    if (r?.frequency) {
        const f = { weekly: "еженедельно", monthly: "ежемесячно", custom: `каждые ${r.interval_days} дн.` }[r.frequency];
        text += `, ${f}${r.until ? ` до ${new Date(r.until).toLocaleDateString("ru-RU")}` : `, ${r.count} раз`}`;
    }
    return text;
}

function describeScope(s) {
    if (!s || s.mode === "all") return "все сотрудники в адаптации";
    const parts = [];
    if (s.departments?.length) parts.push(s.departments.join(", "));
    if (s.positions?.length) parts.push(`должности: ${s.positions.join(", ")}`);
    if (s.enrollment_ids?.length) parts.push(`${s.enrollment_ids.length} сотр.`);
    return parts.join(" · ");
}

function RuleEditor({ rule, state, people, onSaved }) {
    const { showAlert } = useAlertContext();
    const [draft, setDraft] = useState(EMPTY_RULE);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [peopleQuery, setPeopleQuery] = useState("");
    useEffect(() => {
        setDraft(rule ? {
            ...EMPTY_RULE,
            ...rule,
            public_title: rule.public_title || "",
            timing: { ...EMPTY_RULE.timing, ...rule.timing, repeat: rule.timing?.repeat || { frequency: "" } },
            scope: { ...EMPTY_RULE.scope, ...rule.scope },
        } : EMPTY_RULE);
        setPreview(null);
    }, [rule]);
    const setTiming = (patch) => { setDraft((d) => ({ ...d, timing: { ...d.timing, ...patch } })); setPreview(null); };
    const setRepeat = (patch) => setTiming({ repeat: { ...(draft.timing.repeat || {}), ...patch } });
    const setScope = (patch) => { setDraft((d) => ({ ...d, scope: { ...d.scope, ...patch } })); setPreview(null); };
    const setParticipants = (list) => { setDraft((d) => ({ ...d, participants: list })); setPreview(null); };
    const payload = () => ({
        ...draft,
        id: rule?.id,
        timing: { ...draft.timing, repeat: draft.timing.repeat?.frequency ? draft.timing.repeat : undefined },
        participants: draft.participants.map((p) => ({ ...p, form_id: p.form_id ? Number(p.form_id) : p.form_id })),
    });
    const runPreview = async () => {
        setBusy(true);
        try {
            setPreview(await previewRule(payload()));
        } catch (e) {
            showAlert(errorText(e, "Проверьте настройки этапа"), "error");
        } finally {
            setBusy(false);
        }
    };
    const save = async () => {
        setBusy(true);
        try {
            const result = rule?.id ? await updateRule(rule.id, payload()) : await createRule(payload());
            const c = result.sync?.counts || {};
            showAlert(`Этап сохранён: создано ${c.create || 0}, обновлено ${c.update || 0}, снято ${c.remove || 0}`, "success");
            onSaved(result.rule);
        } catch (e) {
            showAlert(errorText(e, "Не удалось сохранить этап"), "error");
        } finally {
            setBusy(false);
        }
    };
    const roles = (state.roles || []).filter((r) => !r.archived);
    const forms = (state.forms || []).filter((f) => !f.archived);
    const t = draft.timing;
    const s = draft.scope;
    const filteredPeople = people.filter((p) => !peopleQuery || `${p.full_name} ${p.department}`.toLowerCase().includes(peopleQuery.toLowerCase()));
    return (
        <div className="space-y-2">
            <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
                <Field label="Название этапа"><input className={input} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="например, «3 месяца»" /></Field>
                <Field label="Заголовок для участника (необязательно)"><input className={input} value={draft.public_title} onChange={(e) => setDraft({ ...draft, public_title: e.target.value })} placeholder="Адаптационный опрос" /></Field>
                <div className="pb-2"><Toggle checked={draft.active} onChange={(v) => { setDraft({ ...draft, active: v }); setPreview(null); }} label="Активен" /></div>
            </div>

            <div className="grid gap-2 xl:grid-cols-2">
                <div className="rounded-md border border-slate-200 p-2">
                    <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">Когда</p>
                    <div className="mb-2 flex flex-wrap gap-1.5">
                        {[["offset", "Через N от выхода"], ["date", "С конкретной даты"], ["standard", "Заменить анкеты стандартного этапа"]].map(([mode, label]) => (
                            <button key={mode} type="button" className={chip(t.mode === mode)} onClick={() => setTiming({ mode })}>{label}</button>
                        ))}
                    </div>
                    {t.mode === "offset" ? (
                        <div className="flex gap-2">
                            <input type="number" min={0} className={`${field} w-24`} value={t.value ?? ""} onChange={(e) => setTiming({ value: e.target.value })} />
                            <select className={`${field} w-auto`} value={t.unit || "months"} onChange={(e) => setTiming({ unit: e.target.value })}>
                                <option value="days">дней</option>
                                <option value="weeks">недель</option>
                                <option value="months">месяцев</option>
                            </select>
                            <span className="self-center text-sm text-slate-500">от даты выхода</span>
                        </div>
                    ) : null}
                    {t.mode === "date" ? <input type="date" className={`${field} w-auto`} value={t.date || ""} onChange={(e) => setTiming({ date: e.target.value })} /> : null}
                    {t.mode === "standard" ? (
                        <select className={`${field} w-auto`} value={t.kind || ""} onChange={(e) => setTiming({ kind: e.target.value })}>
                            <option value="">— стандартный этап —</option>
                            {(state.standard_kinds || []).map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
                        </select>
                    ) : null}
                    {t.mode !== "standard" ? (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            <select className={`${field} w-auto`} value={t.repeat?.frequency || ""} onChange={(e) => setRepeat({ frequency: e.target.value })}>
                                <option value="">без повтора</option>
                                <option value="weekly">повтор еженедельно</option>
                                <option value="monthly">повтор ежемесячно</option>
                                <option value="custom">повтор каждые N дней</option>
                            </select>
                            {t.repeat?.frequency === "custom" ? <input type="number" className={`${field} w-20`} placeholder="дней" value={t.repeat.interval_days || ""} onChange={(e) => setRepeat({ interval_days: e.target.value })} /> : null}
                            {t.repeat?.frequency ? (
                                <>
                                    <input type="number" className={`${field} w-20`} placeholder="раз" value={t.repeat.count || ""} onChange={(e) => setRepeat({ count: e.target.value, until: "" })} />
                                    <span className="text-xs text-slate-500">или до</span>
                                    <input type="date" className={`${field} w-auto`} value={t.repeat.until || ""} onChange={(e) => setRepeat({ until: e.target.value, count: "" })} />
                                </>
                            ) : null}
                        </div>
                    ) : null}
                    {t.mode !== "standard" ? <div className="mt-2"><Toggle checked={t.business_days !== false} onChange={(v) => setTiming({ business_days: v })} label="переносить с выходных на понедельник" /></div> : null}
                </div>

                <div className="rounded-md border border-slate-200 p-2">
                    <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">Кому</p>
                    <div className="mb-2 flex gap-1.5">
                        <button type="button" className={chip(s.mode === "all")} onClick={() => setScope({ mode: "all" })}>Все в адаптации</button>
                        <button type="button" className={chip(s.mode === "filter")} onClick={() => setScope({ mode: "filter" })}>Выбрать</button>
                    </div>
                    {s.mode === "filter" ? (
                        <div className="space-y-2">
                            <div className="flex flex-wrap gap-1">
                                {DEPARTMENTS.map((d) => {
                                    const active = s.departments.includes(d);
                                    return <button key={d} type="button" className={chip(active)} onClick={() => setScope({ departments: active ? s.departments.filter((x) => x !== d) : [...s.departments, d] })}>{d}</button>;
                                })}
                            </div>
                            <input className={input} placeholder="Должности через запятую (по вхождению: «логист»)" value={(s.positions || []).join(", ")} onChange={(e) => setScope({ positions: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} />
                            <div className="rounded-lg border border-slate-200">
                                <input className="w-full border-b border-slate-200 px-2 py-1.5 text-sm outline-none" placeholder={`Сотрудники (${s.enrollment_ids.length} выбрано) — поиск`} value={peopleQuery} onChange={(e) => setPeopleQuery(e.target.value)} />
                                <div className="max-h-56 overflow-y-auto">
                                    {filteredPeople.map((p) => {
                                        const active = s.enrollment_ids.includes(p.enrollment_id);
                                        return (
                                            <label key={p.enrollment_id} className="flex cursor-pointer items-center gap-2 px-2 py-1 text-sm hover:bg-slate-50">
                                                <input type="checkbox" className="accent-amber-500" checked={active} onChange={() => setScope({ enrollment_ids: active ? s.enrollment_ids.filter((x) => x !== p.enrollment_id) : [...s.enrollment_ids, p.enrollment_id] })} />
                                                <span className="flex-1">{p.full_name}</span>
                                                <span className="text-xs text-slate-400">{p.department}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                            <p className="text-xs text-slate-400">Отделы и должности вместе — пересечение; выбранные сотрудники добавляются всегда.</p>
                        </div>
                    ) : null}
                    <div className="mt-2 space-y-1">
                        <Toggle checked={s.include_future} onChange={(v) => setScope({ include_future: v })} label="автоматически для новых сотрудников" />
                        {t.mode !== "standard" ? <Toggle checked={s.include_past_dates} onChange={(v) => setScope({ include_past_dates: v })} label="создавать и с прошедшей датой" /> : null}
                    </div>
                </div>
            </div>

            <div className="rounded-md border border-slate-200 p-2">
                <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">Участники и их анкеты</p>
                <div className="space-y-1.5">
                    {draft.participants.map((p, index) => (
                        <div key={index} className="flex flex-wrap items-center gap-2">
                            <select className={`${field} w-auto min-w-[170px]`} value={p.role} onChange={(e) => setParticipants(draft.participants.map((x, i) => (i === index ? { ...x, role: e.target.value } : x)))}>
                                {roles.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
                            </select>
                            <select className={`${field} min-w-[220px] flex-1`} value={p.form_id || ""} onChange={(e) => setParticipants(draft.participants.map((x, i) => (i === index ? { ...x, form_id: e.target.value } : x)))}>
                                <option value="">— анкета —</option>
                                {forms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                            </select>
                            <Toggle checked={p.required !== false} onChange={(v) => setParticipants(draft.participants.map((x, i) => (i === index ? { ...x, required: v } : x)))} label="обязателен для завершения" />
                            <button type="button" className="px-2 text-slate-400 hover:text-red-600" onClick={() => setParticipants(draft.participants.filter((_, i) => i !== index))}>×</button>
                        </div>
                    ))}
                </div>
                <button type="button" className="mt-2 text-xs font-semibold text-amber-700 hover:underline" onClick={() => {
                    const free = roles.find((r) => !draft.participants.some((p) => p.role === r.code));
                    if (free) setParticipants([...draft.participants, { role: free.code, form_id: "", required: true }]);
                }}>+ участник</button>
            </div>

            <div className="flex flex-wrap gap-2">
                <button type="button" className={btnGhost} disabled={busy} onClick={runPreview}>Предпросмотр</button>
                <button type="button" className={btnPrimary} disabled={busy} onClick={save}>{busy ? "…" : rule?.id ? "Сохранить изменения" : "Создать этап"}</button>
            </div>

            {preview ? (
                <div className="rounded-md border border-amber-200 bg-amber-50/60 p-2">
                    <p className="mb-2 text-sm font-bold text-slate-900">
                        Изменения: создать {preview.counts?.create || 0} · обновить {preview.counts?.update || 0} · снять {preview.counts?.remove || 0}
                        {preview.counts?.conflict ? ` · конфликт ${preview.counts.conflict}` : ""}
                    </p>
                    <div className="max-h-[50vh] overflow-y-auto">
                        <table className="w-full text-sm">
                            <tbody>
                                {(preview.rows || []).map((row) => (
                                    <tr key={row.enrollment_id} className="border-t border-amber-100">
                                        <td className="py-1 pr-2 font-medium">{row.full_name}</td>
                                        <td className="py-1 pr-2 text-xs text-slate-500">{row.department}</td>
                                        <td className="py-1 text-xs">{row.actions.map((a) => `${new Date(a.date).toLocaleDateString("ru-RU")} — ${ACTION_LABELS[a.action] || a.action}`).join("; ")}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {!(preview.rows || []).length ? <p className="text-sm text-slate-500">Сейчас никого не затронет{draft.scope.include_future ? " — этап появится у новых сотрудников" : ""}.</p> : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function StagesTab({ state, reload }) {
    const [selected, setSelected] = useState(null);
    const [people, setPeople] = useState([]);
    useEffect(() => {
        getDashboard({}).then((data) => {
            const map = new Map();
            (data?.rows || []).forEach((r) => { if (!map.has(r.enrollment_id)) map.set(r.enrollment_id, r); });
            setPeople([...map.values()].sort((a, b) => a.full_name.localeCompare(b.full_name, "ru")));
        }).catch(() => setPeople([]));
    }, []);
    const rules = state.rules || [];
    const current = selected === "new" ? null : rules.find((r) => r.id === selected) || null;
    const roleName = (code) => (state.roles || []).find((r) => r.code === code)?.name || code;
    return (
        <Pane
            list={
                <div>
                    <div className="sticky top-0 z-10 border-b border-slate-200 bg-white p-2">
                        <button type="button" className={`${btnPrimary} w-full`} onClick={() => setSelected("new")}>+ Новый этап</button>
                    </div>
                    {rules.map((r) => (
                        <ListItem key={r.id} active={selected === r.id} onClick={() => setSelected(r.id)} title={r.title}
                            meta={`${describeTiming(r.timing, state.standard_kinds)} · ${describeScope(r.scope)} · ${(r.participants || []).map((p) => roleName(p.role)).join(", ")}`}
                            badge={r.active ? null : "выкл"} />
                    ))}
                    {!rules.length ? <p className="p-4 text-sm text-slate-500">Своих этапов пока нет. Стандартные (1 неделя, 1 и 2 месяца) работают как раньше.</p> : null}
                </div>
            }
            editor={
                selected == null ? (
                    <div className="space-y-2 text-sm text-slate-600">
                        <p><b>Этап</b> — когда и кому отправить анкеты, и кто их заполняет.</p>
                        <p>Можно: добавить этап на любой срок от выхода, с конкретной даты, с повтором; для всех, отделов, должностей или отдельных людей; добавить своих участников (например, наставника) и заменить анкеты стандартного этапа.</p>
                        <p>Уже отправленные и заполненные анкеты не меняются — правки действуют только на будущие этапы.</p>
                    </div>
                ) : (
                    <RuleEditor rule={current} state={state} people={people} onSaved={async (saved) => { await reload(); if (saved?.id) setSelected(saved.id); }} />
                )
            }
        />
    );
}

// ------------------------------------------------------------------------- page

export default function AdaptationBuilderPage() {
    const { showAlert } = useAlertContext();
    const [tab, setTab] = useState("stages");
    const [state, setState] = useState(null);
    const [help, setHelp] = useState(false);
    const reload = useCallback(async () => {
        try {
            setState(await getBuilderState());
        } catch (e) {
            showAlert(errorText(e, "Не удалось загрузить конструктор"), "error");
            setState((prev) => prev || { questions: [], roles: [], forms: [], rules: [] });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => { reload(); }, [reload]);
    return (
        <MainLayout className="!px-3 !py-2 bg-slate-50/70" contentClassName="flex h-[calc(100vh-4.5rem)] flex-col">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                    <Link to="/tests/adaptation" className="text-sm text-slate-500 hover:text-slate-900">← Адаптация</Link>
                    <h1 className="text-lg font-bold text-slate-900">Конструктор адаптации</h1>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex rounded-md bg-slate-200/70 p-0.5">
                        {TABS.map(([key, label]) => (
                            <button key={key} type="button" onClick={() => setTab(key)}
                                className={`rounded px-3 py-1 text-sm font-semibold transition ${tab === key ? "bg-white text-slate-900 shadow" : "text-slate-600 hover:text-slate-900"}`}>
                                {label}
                            </button>
                        ))}
                    </div>
                    <Link to="/tests/adaptation/dashboard" className={btnGhost}>Дашборд</Link>
                    <button type="button" className={btnGhost} onClick={() => setHelp(true)} data-testid="adaptation-help">? Справка</button>
                </div>
            </div>
            <AdaptationHelp open={help} onClose={() => setHelp(false)} section={tab} />
            {!state ? <p className="text-sm text-slate-500">Загрузка…</p> : (
                <div className="flex min-h-0 flex-1 flex-col">
                    {tab === "stages" ? <StagesTab state={state} reload={reload} /> : null}
                    {tab === "forms" ? <FormsTab state={state} reload={reload} /> : null}
                    {tab === "questions" ? <QuestionsTab state={state} reload={reload} /> : null}
                    {tab === "roles" ? <RolesTab state={state} reload={reload} /> : null}
                </div>
            )}
        </MainLayout>
    );
}
